<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import MasifiWhatsappQrAPI from 'dashboard/api/masifi/masifiWhatsappQr';
import {
  MASIFI_WA_CALL_REPLY_MAX,
  MASIFI_WA_HISTORY_DAYS,
  MASIFI_WA_MARK_READ_MODES,
  MASIFI_WA_PRESENCE_MODES,
} from 'dashboard/helper/masifi/masifiWhatsappQr';

// Ajustes POR NÚMERO de WhatsApp QR (spec whatsapp-qr-polish-1 §4), espejo de Masifi::WhatsappQr::NumberSettings: cada
// cambio guarda SOLO su clave (PATCH settings) y emite la respuesta; si falla, vuelve atrás. Admin-only (lo es la API).
const props = defineProps({
  inboxId: { type: Number, required: true },
  settings: { type: Object, required: true },
  // Fase 2 (spec §4): el knob de la cuenta whatsapp_qr_history_import (payload `history_available`).
  historyAvailable: { type: Boolean, default: false },
});
const emit = defineEmits(['saved']);
const { t } = useI18n();

const current = ref({ ...props.settings });
const callReply = ref(props.settings.call_reply || '');
watch(
  () => props.settings,
  value => {
    current.value = { ...value };
    callReply.value = value.call_reply || '';
  }
);

// Claves i18n estáticas (lint @intlify/vue-i18n/no-dynamic-keys); los valores salen de la fuente única YAML.
const markReadLabels = computed(() => ({
  on_open: t('MASIFI_WHATSAPP_QR.NUMBER_SETTINGS.MARK_READ.ON_OPEN'),
  on_reply: t('MASIFI_WHATSAPP_QR.NUMBER_SETTINGS.MARK_READ.ON_REPLY'),
  never: t('MASIFI_WHATSAPP_QR.NUMBER_SETTINGS.MARK_READ.NEVER'),
}));
const presenceLabels = computed(() => ({
  online: t('MASIFI_WHATSAPP_QR.NUMBER_SETTINGS.PRESENCE.ONLINE'),
  offline: t('MASIFI_WHATSAPP_QR.NUMBER_SETTINGS.PRESENCE.OFFLINE'),
}));
const markReadOptions = computed(() =>
  MASIFI_WA_MARK_READ_MODES.map(value => ({
    value,
    label: markReadLabels.value[value],
  }))
);
const presenceOptions = computed(() =>
  MASIFI_WA_PRESENCE_MODES.map(value => ({
    value,
    label: presenceLabels.value[value],
  }))
);
// Fase 2 (§3.2): días hacia atrás del historial (fuente única YAML: history_days.values).
const historyDaysOptions = computed(() =>
  MASIFI_WA_HISTORY_DAYS.map(value => ({
    value,
    label: t('MASIFI_WHATSAPP_QR.NUMBER_SETTINGS.HISTORY_DAYS.OPTION', {
      days: value,
    }),
  }))
);
const isOnline = computed(() => current.value.presence === 'online');
const replyChanged = computed(
  () => callReply.value.trim() !== (current.value.call_reply || '')
);

const save = async (key, value) => {
  const previous = current.value[key];
  current.value = { ...current.value, [key]: value };
  try {
    const { data } = await MasifiWhatsappQrAPI.updateSettings(props.inboxId, {
      [key]: value,
    });
    emit('saved', data);
  } catch (error) {
    current.value = { ...current.value, [key]: previous };
    useAlert(
      error?.response?.data?.error || t('MASIFI_WHATSAPP_QR.ERRORS.GENERIC')
    );
  }
};
const saveCallReply = () => save('call_reply', callReply.value.trim());
</script>

<template>
  <div class="flex flex-col gap-4">
    <SettingsToggleSection
      hide-toggle
      :header="t('MASIFI_WHATSAPP_QR.NUMBER_SETTINGS.MARK_READ.HEADER')"
      :description="
        t('MASIFI_WHATSAPP_QR.NUMBER_SETTINGS.MARK_READ.DESCRIPTION')
      "
    >
      <template #hiddenToggle>
        <Select
          :model-value="current.mark_read"
          :options="markReadOptions"
          :aria-label="t('MASIFI_WHATSAPP_QR.NUMBER_SETTINGS.MARK_READ.HEADER')"
          @update:model-value="value => save('mark_read', value)"
        />
      </template>
    </SettingsToggleSection>
    <SettingsToggleSection
      hide-toggle
      :header="t('MASIFI_WHATSAPP_QR.NUMBER_SETTINGS.PRESENCE.HEADER')"
      :description="
        t('MASIFI_WHATSAPP_QR.NUMBER_SETTINGS.PRESENCE.DESCRIPTION')
      "
    >
      <template #hiddenToggle>
        <Select
          :model-value="current.presence"
          :options="presenceOptions"
          :aria-label="t('MASIFI_WHATSAPP_QR.NUMBER_SETTINGS.PRESENCE.HEADER')"
          @update:model-value="value => save('presence', value)"
        />
      </template>
    </SettingsToggleSection>
    <SettingsToggleSection
      v-if="isOnline"
      :model-value="current.typing_out"
      :header="t('MASIFI_WHATSAPP_QR.NUMBER_SETTINGS.TYPING_OUT.HEADER')"
      :description="
        t('MASIFI_WHATSAPP_QR.NUMBER_SETTINGS.TYPING_OUT.DESCRIPTION')
      "
      @update:model-value="value => save('typing_out', value)"
    />
    <SettingsToggleSection
      :model-value="current.show_reactions"
      :header="t('MASIFI_WHATSAPP_QR.SETTINGS.SHOW_REACTIONS')"
      :description="t('MASIFI_WHATSAPP_QR.SETTINGS.SHOW_REACTIONS_HELP')"
      @update:model-value="value => save('show_reactions', value)"
    />
    <SettingsToggleSection
      :model-value="current.groups"
      :header="t('MASIFI_WHATSAPP_QR.SETTINGS.RECEIVE_GROUPS')"
      :description="t('MASIFI_WHATSAPP_QR.SETTINGS.RECEIVE_GROUPS_HELP')"
      @update:model-value="value => save('groups', value)"
    />
    <SettingsToggleSection
      v-if="historyAvailable"
      :model-value="current.history_import"
      :header="t('MASIFI_WHATSAPP_QR.NUMBER_SETTINGS.HISTORY_IMPORT.HEADER')"
      :description="
        t('MASIFI_WHATSAPP_QR.NUMBER_SETTINGS.HISTORY_IMPORT.DESCRIPTION')
      "
      @update:model-value="value => save('history_import', value)"
    >
      <template v-if="current.history_import" #editor>
        <Select
          :model-value="current.history_days"
          :options="historyDaysOptions"
          :aria-label="
            t('MASIFI_WHATSAPP_QR.NUMBER_SETTINGS.HISTORY_DAYS.HEADER')
          "
          @update:model-value="value => save('history_days', value)"
        />
      </template>
    </SettingsToggleSection>
    <SettingsToggleSection
      :model-value="current.calls_log"
      :header="t('MASIFI_WHATSAPP_QR.NUMBER_SETTINGS.CALLS_LOG.HEADER')"
      :description="
        t('MASIFI_WHATSAPP_QR.NUMBER_SETTINGS.CALLS_LOG.DESCRIPTION')
      "
      @update:model-value="value => save('calls_log', value)"
    />
    <SettingsToggleSection
      :model-value="current.reject_calls"
      :header="t('MASIFI_WHATSAPP_QR.NUMBER_SETTINGS.REJECT_CALLS.HEADER')"
      :description="
        t('MASIFI_WHATSAPP_QR.NUMBER_SETTINGS.REJECT_CALLS.DESCRIPTION')
      "
      @update:model-value="value => save('reject_calls', value)"
    >
      <template v-if="current.reject_calls" #editor>
        <div class="flex flex-col gap-2">
          <TextArea
            :id="`masifi-wa-call-reply-${inboxId}`"
            v-model="callReply"
            :label="t('MASIFI_WHATSAPP_QR.NUMBER_SETTINGS.CALL_REPLY.LABEL')"
            :placeholder="
              t('MASIFI_WHATSAPP_QR.NUMBER_SETTINGS.CALL_REPLY.PLACEHOLDER')
            "
            :max-length="MASIFI_WA_CALL_REPLY_MAX"
            auto-height
          />
          <p class="text-sm text-n-slate-11">
            {{ t('MASIFI_WHATSAPP_QR.NUMBER_SETTINGS.CALL_REPLY.HELP') }}
          </p>
          <div>
            <NextButton
              data-test="wa-call-reply-save"
              :label="t('MASIFI_WHATSAPP_QR.NUMBER_SETTINGS.CALL_REPLY.SAVE')"
              :disabled="!replyChanged"
              @click="saveCallReply"
            />
          </div>
        </div>
      </template>
    </SettingsToggleSection>
  </div>
</template>
